package com.zq.webproject.ui.ranking

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.zq.webproject.model.RankingUser
import org.jetbrains.compose.resources.painterResource
import webproject.composeapp.generated.resources.Res
import webproject.composeapp.generated.resources.icon_no
import webproject.composeapp.generated.resources.icon_no01
import webproject.composeapp.generated.resources.icon_no2
import webproject.composeapp.generated.resources.icon_no3
import webproject.composeapp.generated.resources.meiyoushuju
import webproject.composeapp.generated.resources.paihangbang


// 字体定义
private val CaiFont: FontFamily
    @Composable
    get() = FontFamily.Default

/**
 * 收入排行榜页面
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RankingScreen(
    onBackClick: () -> Unit
) {
    println("RankingScreen - 渲染排行榜页面")

    val viewModel = remember { RankingViewModel() }

    // 页面加载时获取排行榜数据和我的收入
    LaunchedEffect(Unit) {
        println("RankingScreen - 开始加载排行榜数据")
        viewModel.loadRanking()
        // 加载我的收入
        viewModel.loadMyIncome()
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("收入排行榜", fontFamily = CaiFont, fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = {
                        println("RankingScreen - 点击返回按钮")
                        onBackClick()
                    }) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, "返回")
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color(0xFFFFD700),  // 金色背景
                    titleContentColor = Color.White,
                    navigationIconContentColor = Color.White
                )
            )
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF5F5F5))
        ) {
            when {
                viewModel.isLoading -> {
                    // 加载中
                    CircularProgressIndicator(
                        modifier = Modifier.align(Alignment.Center),
                        color = Color(0xFFFFD700)
                    )
                }
                viewModel.errorMessage != null -> {
                    // 错误状态
                    Column(
                        modifier = Modifier.align(Alignment.Center),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Image(painter = painterResource(Res.drawable.meiyoushuju),contentDescription = null)
                        Text(
                            text = viewModel.errorMessage ?: "加载失败",
                            fontSize = 16.sp,
                            color = Color.Gray,
                            fontFamily = CaiFont
                        )
                        Button(
                            onClick = { viewModel.loadRanking() },
                            modifier = Modifier.padding(top = 16.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = Color(0xFFFFD700)
                            )
                        ) {
                            Text("重试", fontFamily = CaiFont)
                        }
                    }
                }
                viewModel.rankingList.isEmpty() -> {
                    // 空状态
                    Column(
                        modifier = Modifier.align(Alignment.Center),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Image(
                            painter = painterResource(Res.drawable.icon_no),
                            contentDescription = null,
                            modifier = Modifier.size(35.dp)
                        )
                        Text(
                            text = "暂无排行榜数据",
                            fontSize = 16.sp,
                            color = Color.Gray,
                            fontFamily = CaiFont
                        )
                    }
                }
                else -> {
                    // 显示排行榜内容
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        // 统计卡片
                        item {
                            StatisticsCard(
                                totalUsers = viewModel.rankingList.size,
                                myRank = viewModel.myRank,
                                myIncome = viewModel.myIncome,
                                percentile = viewModel.getPercentile()
                            )
                        }

                        // 分隔线
                        item {
                            Divider(
                                modifier = Modifier.padding(vertical = 8.dp),
                                color = Color(0xFFEEEEEE)
                            )
                        }

                        // 排行榜标题
                        item {
                            Row(){
                                Image(
                                    painter = painterResource(Res.drawable.icon_no),
                                    contentDescription = null,
                                    modifier = Modifier.size(35.dp)
                                )
                                Text(
                                    text = " 排行榜",
                                    fontSize = 18.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = Color(0xFF333333),
                                    modifier = Modifier.padding(vertical = 8.dp)
                                    // 注意：包含emoji的文本不能使用自定义中文字体，否则emoji不显示
                                )
                            }

                        }

                        // 排行榜列表
                        itemsIndexed(viewModel.rankingList) { index, user ->
                            RankingItem(
                                rank = index + 1,
                                user = user,
                                isMyRank = index + 1 == viewModel.myRank
                            )
                        }
                    }
                }
            }
        }
    }
}

/**
 * 统计卡片
 */
@Composable
fun StatisticsCard(
    totalUsers: Int,
    myRank: Int?,
    myIncome: Double?,
    percentile: Int?
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(
            containerColor = Color.White
        ),
        elevation = CardDefaults.cardElevation(4.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(20.dp)
        ) {
            Row(){
                Image(
                    painter = painterResource(Res.drawable.paihangbang),
                    contentDescription = null,
                    modifier = Modifier.width(55.dp).height(25.dp)
                )

                Text(
                    text = "排行榜统计",
                    fontSize = 16.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF333333),
                    modifier = Modifier.padding(bottom = 16.dp)
                    // 注意：包含emoji的文本不能使用自定义中文字体，否则emoji不显示
                )

            }


            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceEvenly
            ) {
                // 我的排名
                StatItem(
                    label = "我的排名",
                    value = if (myRank != null) "第 $myRank 名" else "未上榜",
                    color = if (myRank != null && myRank <= 10) Color(0xFFFFD700) else Color(0xFF666666)
                )

                Divider(
                    modifier = Modifier
                        .width(1.dp)
                        .height(50.dp),
                    color = Color(0xFFEEEEEE)
                )

                // 我的收入
                StatItem(
                    label = "我的收入",
                    value = myIncome?.toString() ?: "0.0",
                    color = Color(0xFFFF5722)
                )
        }

            // 超越百分比
            if (percentile != null) {
                Spacer(modifier = Modifier.height(16.dp))
                Text(
                    text = "🎉 您已超越 $percentile% 的用户！",
                    fontSize = 14.sp,
                    color = Color(0xFFFF5722),
                    modifier = Modifier.align(Alignment.CenterHorizontally)
                    // 注意：包含emoji的文本不能使用自定义中文字体，否则emoji不显示
                )
            }
        }
    }
}

/**
 * 统计项
 */
@Composable
fun StatItem(
    label: String,
    value: String,
    color: Color
) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = label,
            fontSize = 12.sp,
            color = Color(0xFF999999),
            fontFamily = CaiFont
        )
        Spacer(modifier = Modifier.height(8.dp))
        Text(
            text = value,
            fontSize = 16.sp,
            fontWeight = FontWeight.Bold,
            color = color,
            fontFamily = CaiFont
        )
    }
}

/**
 * 排行榜项
 */
@Composable
fun RankingItem(
    rank: Int,
    user: RankingUser,
    isMyRank: Boolean
) {
    val backgroundColor = when {
        isMyRank -> Color(0xFFFFF8DC)  // 我的排名：浅黄色
        rank == 1 -> Color(0xFFFFFFE0)  // 第1名：金色背景
        rank == 2 -> Color(0xFFF5F5F5)  // 第2名：银色背景
        rank == 3 -> Color(0xFFFFEBCD)  // 第3名：铜色背景
        else -> Color.White
    }

    val rankColor = when (rank) {
        1 -> Color(0xFFFFD700)  // 金色
        2 -> Color(0xFFC0C0C0)  // 银色
        3 -> Color(0xFFCD7F32)  // 铜色
        else -> Color(0xFF666666)
    }

    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(
            containerColor = backgroundColor
        ),
        elevation = CardDefaults.cardElevation(if (rank <= 3) 6.dp else 2.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // 排名徽章
            RankBadge(rank = rank, color = rankColor)

            Spacer(modifier = Modifier.width(16.dp))

            // 用户头像
            Box(
                modifier = Modifier
                    .size(48.dp)
                    .clip(CircleShape)
                    .background(
                        brush = Brush.verticalGradient(
                            colors = listOf(Color(0xFF1E88E5), Color(0xFF1565C0))
                        )
                    ),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = user.username?.firstOrNull()?.toString()?.uppercase() ?: "?",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color.White,
                    fontFamily = CaiFont
                )
            }

            Spacer(modifier = Modifier.width(16.dp))

            // 用户信息
            Column(
                modifier = Modifier.weight(1f)
            ) {
                Text(
                    text = user.username ?: "未知用户",
                    fontSize = 16.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF333333),
                    fontFamily = CaiFont
                )
                if (isMyRank) {
                    Text(
                        text = "这是您",
                        fontSize = 12.sp,
                        color = Color(0xFFFF5722),
                        fontFamily = CaiFont,
                        modifier = Modifier.padding(top = 4.dp)
                    )
                }
            }

            // 收入金额
            Text(
                text = "¥"+ user.totalIncome.toString(),
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
                color = Color(0xFFFF5722),
                fontFamily = CaiFont
            )
        }
    }
}

/**
 * 排名徽章
 */
@Composable
fun RankBadge(rank: Int, color: Color) {
    // 排名图标
    val icon = when (rank) {
        1 ->Res.drawable.icon_no01
        2 -> Res.drawable.icon_no2
        3 -> Res.drawable.icon_no3
        else ->Res.drawable.icon_no
    }



    Box(
        modifier = Modifier
            .size(48.dp)
            .clip(CircleShape)
            .background(color.copy(alpha = 0.2f)),
        contentAlignment = Alignment.Center
    ) {
        if (rank<4) {
            Image(
                painter = painterResource(icon),
                contentDescription = null,
                modifier = Modifier.size(35.dp)
            )
        } else {
            Text(
                text = "$rank",
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
                color = color,
                fontFamily = CaiFont
            )
        }
    }
}